import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import DeleteModal from "../../components/Common/DeleteModal";

import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  FormGroup,
  Label,
  Button,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
} from "reactstrap";

import { ToastContainer } from "react-toastify";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { map } from "lodash";

import ActionEdit from "components/buttons/actionEdit";
import ActionDelete from "components/buttons/actionDelete";
import SafeField from "helpers/SafeField";

import {
  addProjectType,
  getProjectType,
  updateProjectType,
  deleteProjectType,
} from "../../store/actions";

const ProjectType = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { protypes, loading } = useSelector(
    (state) => state.ObjectReducer
  );

  const [protype, setProtype] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [isRight, setIsRight] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);

  // =============================
  // Load Project Types
  // =============================
  useEffect(() => {
    const userId = localStorage.getItem("userId");
    dispatch(getProjectType(userId));
  }, [dispatch]);

  // =============================
  // Canvas Controls
  // =============================
  const openEditCanvas = (data) => {
    setProtype({
      id: data.id,
      projectTypeId: data.projectTypeId,
      projectTypeTitle: data.projectTypeTitle,
      agentId: data.agentId,
    });
    setIsEdit(true);
    setIsRight(true);
  };

  const closeCanvas = () => {
    setProtype({});
    setIsEdit(false);
    setIsRight(false);
  };

  // =============================
  // Add Submit
  // =============================
  const handleProTypeSubmit = (values, { resetForm }) => {
    dispatch(addProjectType(values, history));
    resetForm();
  };

  // =============================
  // Delete
  // =============================
  const onClickDelete = (item) => {
    setProtype(item);
    setDeleteModal(true);
  };

  const handleDeleteProType = () => {
    if (protype?.id) {
      dispatch(deleteProjectType(protype));
      setDeleteModal(false);
    }
  };

  // =============================
  // Update Submit
  // =============================
  const handleUpdate = (values) => {
    const updateData = {
      id: values.id,
      projectTypeId: values.projectTypeId,
      projectTypeTitle: values.projectTypeTitle,
      agentId: values.agentId,
    };

    dispatch(updateProjectType(updateData));
    closeCanvas();
  };

  return (
    <React.Fragment>
      {loading && <div className="loading">Loading…</div>}
      <ToastContainer autoClose={2000} />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteProType}
        onCloseClick={() => setDeleteModal(false)}
      />

      <div className="page-content">
       
        <Container fluid>
          <Row className="mb20">
            <Col xl="12">
              <Breadcrumbs
                title={t("General Settings")}
                breadcrumbItem={t("Project Type")}
              />
            </Col>
          </Row>

          <Row>
           
            <Col xl="12">
              {/* ================= Add Form ================= */}
              <Formik
                initialValues={{ projectTypeTitle: "" }}
                validationSchema={Yup.object({
                  projectTypeTitle: Yup.string().required("This is required"),
                })}
                onSubmit={handleProTypeSubmit}
              >
                {({ errors, touched, handleChange }) => (
                  <Form className="needs-validation">
                    <Card>
                      <CardBody>
                        
                        <Row>
                          <Col sm="3">
                            <FormGroup className="mb-3">
                              <Label>{t("Title *")}</Label>

                              <SafeField
                                name="projectTypeTitle"
                                type="text"
                                onChange={handleChange}
                                className={
                                  "form-control" +
                                  (errors.projectTypeTitle &&
                                  touched.projectTypeTitle
                                    ? " is-invalid"
                                    : "")
                                }
                              />

                              <ErrorMessage
                                name="projectTypeTitle"
                                component="div"
                                className="invalid-feedback"
                              />
                            </FormGroup>
                          </Col>

                          <Col sm="2">
                            <Button
                              type="submit"
                              color="primary"
                              style={{ marginTop: 31 }}
                            >
                              Add
                            </Button>
                          </Col>
                        </Row>
                      </CardBody>
                    </Card>

                    {/* ================= History ================= */}
                    <Card>
                      <CardBody>
                        <h5 className="card-title">History</h5>

                        <div className="table-responsive">
                          <table className="table table-bordered">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Title</th>
                                <th>Action</th>
                              </tr>
                            </thead>

                            <tbody>
                              {map(protypes, (item, index) => (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td>{item.projectTypeTitle}</td>
                                  <td>
                                    <ActionEdit
                                      onClick={() => openEditCanvas(item)}
                                    />
                                    <ActionDelete
                                      onClick={() => onClickDelete(item)}
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </CardBody>
                    </Card>
                  </Form>
                )}
              </Formik>
            </Col>
          </Row>

          {/* ================= Edit Offcanvas ================= */}
          <Offcanvas isOpen={isRight} direction="end" toggle={closeCanvas}>
            <OffcanvasHeader toggle={closeCanvas}>
              Update
            </OffcanvasHeader>

            <OffcanvasBody>
              <Formik
                enableReinitialize
                initialValues={{
                  id: protype?.id,
                  projectTypeId: protype?.projectTypeId,
                  projectTypeTitle: protype?.projectTypeTitle || "",
                  agentId: protype?.agentId,
                }}
                validationSchema={Yup.object({
                  projectTypeTitle: Yup.string().required("This is required"),
                })}
                onSubmit={handleUpdate}
              >
                {({ errors, touched, handleChange }) => (
                  <Form>
                    <FormGroup className="mb-3">
                      <Label>{t("Title *")}</Label>

                      <SafeField
                        name="projectTypeTitle"
                        type="text"
                        onChange={handleChange}
                        className={
                          "form-control" +
                          (errors.projectTypeTitle &&
                          touched.projectTypeTitle
                            ? " is-invalid"
                            : "")
                        }
                      />

                      <ErrorMessage
                        name="projectTypeTitle"
                        component="div"
                        className="invalid-feedback"
                      />
                    </FormGroup>

                    <Button type="submit" color="primary">
                      Update
                    </Button>
                  </Form>
                )}
              </Formik>
            </OffcanvasBody>
          </Offcanvas>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ProjectType;
